<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { historyCommonConfig } from './configs/common'
import { api } from '@/api'
import { brand } from '@/config/brands'
import { useReadStore, useToastStore, useTrackValueStore } from '@/stores'
import { TRACK_CHANNEL_NAME_VALUE } from '@/constants'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

type Book = GetReadHistoryListResponse['data']['records'][0]

const config = historyCommonConfig

const { $toast } = useToastStore()
const readStore = useReadStore()

const history = ref<Book[]>([])
const page = ref(1)
const total = ref<number>(0)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))
async function fetchHistory() {
  fetching.value = true
  try {
    const { data } = await api.get<any, GetReadHistoryListResponse>('/read/history/list', { params: { ...pagination.value } })
    history.value = [...history.value, ...data.records]
    total.value = data.total
    if (page.value >= data.pages)
      ending.value = true
    else
      page.value += 1
  }
  catch (_error) {
    error.value = _error
  }
  finally {
    fetching.value = false
  }
}
const infiniteRef = ref<HTMLElement>()
useInfiniteScroll(infiniteRef,
  async () => {
    if (fetching.value || ending.value || error.value)
      return
    await fetchHistory()
  },
  { distance: 50 },
)

const route = useRoute()
const from = computed(() => {
  return route.query.from as string
})

const router = useRouter()

function gotoChapter(item: Book) {
  if (item.offlineFlag)
    return $toast(config.texts.bookRemoved)
  readStore.update({
    contentId: item.contentId,
    chapterId: item.contentChapterId,
  })
  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId: item.contentId, firstOrigin: 4, originName: 4, chlName: TRACK_CHANNEL_NAME_VALUE.READ_HISTORY })

  router.push({
    name: READER_ROUTE_NAME,
    params: {
      contentId: item.contentId,
      chapterId: item.contentChapterId,
    },
  })
}

const isTalewave = computed(() => brand === 'talewave')
</script>

<template>
  <div ref="infiniteRef" class="pb-20 h-100dvh overflow-scroll" :class="isTalewave ? 'talewave-history-root' : 'bg-n-bg-global'">
    <div v-if="from">
      <BasicNavbar />
      <div class="h-5" />
    </div>
    <!-- Fist Loading -->
    <div v-if="fetching && page === 1" />
    <div v-else-if="history.length === 0" class="mt-11 pt-15vh w-full text-center flex flex-col justify-center">
      <div>
        <img class="mx-a w-100px" src="@/assets/cover-empty.png" alt="图片:空">
        <p class="mt-14px text-13px text-n-text-2">
          {{ config.texts.noData }}
        </p>
        <div class="mt-50px">
          <router-link
            :to="{ name: 'home' }" replace
            class="inline-block text-n-primary text-14px py-2 px-4 border border-solid border-n-primary rounded-full"
          >
            {{ config.texts.goToNovel }}
          </router-link>
        </div>
      </div>
    </div>
    <div v-else :class="isTalewave ? 'talewave-history-page' : 'p-5'">
      <p class="text-14px text-n-text-2" :class="[isTalewave ? 'mb-12px' : '-mb-10px']">
        {{ config.texts.viewed }}
      </p>

      <div v-if="isTalewave" class="talewave-history-grid">
        <div v-for="item in history" :key="item.contentId" class="talewave-item" @click="gotoChapter(item)">
          <div class="cover-wrap">
            <img :src="item.contentCoverUrl" alt="cover" class="cover">
          </div>
          <p class="title text-n-text-1 line-clamp-2">
            {{ item.contentName }}
          </p>
          <p class="meta text-n-text-3">
            {{ `${item.sequenceNo || 1}/${item.chapterNum || '-'}` }} chapters
          </p>
        </div>
      </div>

      <div v-else>
        <div v-for="item in history" :key="item.contentId">
          <div
            class="mt-5 flex items-center space-x-15px"
            @click="gotoChapter(item)"
          >
            <div class="relative">
              <img class="w-81px h-108px rounded-3px" :src="item.contentCoverUrl">
            </div>
            <div class="flex-1">
              <p class="text-15px font-bold line-clamp-1 text-n-text-1">
                {{ item.contentName }}
              </p>
              <p class="text-13px mt-2 text-n-text-3">
                <span>{{ item.finishStatus ? config.texts.completed : config.texts.ongoing }}</span>
              </p>
              <p class="text-13px mt-5px text-n-text-3">
                {{ config.texts.lastRead }}：{{ item.contentChapterName }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="ending" class="py-10 flex space-x-2.5 items-center justify-center">
        <span class="text-14px text-n-text-3">{{ config.texts.noMore }}</span>
      </div>
      <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3">
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>
    </div>

    <BasicTabbar v-if="!from" tab="history" />
  </div>
</template>

<style scoped>
.talewave-history-page {
  padding: 16px 13px 20px;
}

.talewave-history-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 13px;
  row-gap: 24px;
}

.talewave-item {
  cursor: pointer;
}

.talewave-item .cover-wrap {
  width: 100%;
  padding-top: 133%; /* 3:4 封面比例 -> 1.33 */
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

.talewave-item .cover {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.talewave-item .title {
  margin-top: 8px;
  min-height: 44px;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  word-break: break-word;
}

.talewave-item .meta {
  margin-top: 6px;
  color: #6A6E7A;
  font-size: 13px;
  line-height: 18px;
}

/* ensure cover sits below title block visually when designs put title above cover */
.talewave-item .cover-wrap {
  margin-top: 6px;
}

/* talewave history page background gradient (top -> bottom) */
.talewave-history-root {
  background-color: #F6F7F9;
  background-image: linear-gradient(180deg, #CFE4FF 0%, rgba(255, 255, 255, 0) 90px);
}
</style>
